// Multi-pair picker for reusable scan invites. One reusable link can cover
// several custom-made pairs on an order; the customer selects which pair(s)
// THIS scan is for, then POSTs /start to open a ScanSession before capture.
//
// Custom shoes carry no size, so pairs are disambiguated the way the customer
// recognises them physically: the shoe MODEL leads, followed by the full
// colour breakdown (OUTER/INNER/PLUGS/BAND swatches), the recipient name when
// set, and a "Paar N" label (stable with the operator's paperwork numbering).
// When the payload predates the enriched `colours` field we fall back to the
// single BAND colour so a swatch still shows. Shopify order + internal `state`
// are DEBUG-only.
//
// Multi-select is intentional (one scan → N pairs). At least one pair must be
// selected.
//
// An invite that asks for several scan runs (`runsRequired > 1`) says so up
// front, and a pair that already has some of them shows how many: that is the
// customer coming back to a link they left half way.

import { useState } from "react";

import { COLOUR_SLOTS, type ColourSlot, type PairColours, type ScanPair } from "../lib/api";
import { localiseColour } from "../lib/colours";
import { DEBUG } from "../lib/config";
import { useI18n, type TransKey } from "../lib/i18n";

const PANEL = "rounded-xl border border-white/10 bg-white/5 p-4";
const PRIMARY_BTN =
  "w-full rounded-full bg-white py-3.5 text-base font-semibold text-slate-900 shadow-lg transition active:bg-slate-100 disabled:opacity-40";

const SLOT_LABEL_KEY: Record<ColourSlot, TransKey> = {
  OUTER: "picker.slotOuter",
  INNER: "picker.slotInner",
  PLUGS: "picker.slotPlugs",
  BAND: "picker.slotBand",
};

/**
 * Colours to render — only the enriched per-slot breakdown. We deliberately
 * do NOT fall back to the legacy single `colour` (bandColor): it's just the
 * strap (one of four slots, and the misleading one that reads as "the shoe
 * colour"), and the payload carries no hex for it, so it can only render an
 * empty ring. Until hezo-ops sends `colours` (see hezo-ops#708) the row shows
 * model + "Paar N" only, which already disambiguates; real swatches appear
 * automatically once the enriched field lands.
 */
function colourSlotsFor(pair: ScanPair): PairColours | undefined {
  return pair.colours;
}

function ColourRows({ colours }: { colours: PairColours }): React.ReactElement {
  const { t, lang } = useI18n();
  const present = COLOUR_SLOTS.filter((s) => colours[s]);
  return (
    <div className="mt-2 grid grid-cols-[auto_auto_1fr] items-center gap-x-3 gap-y-2 text-sm">
      {present.map((slot) => {
        const c = colours[slot]!;
        return (
          <div key={slot} className="contents" data-testid={`pair-colour-${slot}`}>
            <span className="text-slate-400">{t(SLOT_LABEL_KEY[slot])}</span>
            <span
              className="h-6 w-12 rounded-md"
              // Wide colour bar (more surface than a dot reads the hue better).
              // Dual ring — dark inset + light outer edge — so pale, low-contrast
              // colours (sage, cream, light grey, white) still show a crisp
              // boundary on the dark panel.
              style={{
                backgroundColor: c.hex || "transparent",
                boxShadow:
                  "inset 0 0 0 1px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,255,255,0.4)",
              }}
              aria-hidden
            />
            <span className="text-slate-200">{localiseColour(c, lang)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function PairPicker({
  pairs,
  starting,
  errorMessage,
  onStart,
}: {
  pairs: ScanPair[];
  starting: boolean;
  errorMessage?: string;
  onStart: (pairIds: string[]) => void;
}): React.ReactElement {
  const { t } = useI18n();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectedIds = pairs.map((p) => p.id).filter((id) => selected.has(id));
  const canStart = selectedIds.length > 0 && !starting;
  const runsRequired = Math.max(0, ...pairs.map((p) => p.runsRequired ?? 0));

  return (
    <div className="space-y-5" data-testid="pair-picker">
      <section>
        <h2 className="text-lg font-semibold text-white">{t("picker.title")}</h2>
        <p className="mt-2 text-sm text-slate-300">{t("picker.intro")}</p>
        <p className="mt-1 text-sm text-slate-400">{t("picker.selectHint")}</p>
        {runsRequired > 1 && (
          <p className="mt-3 text-sm text-amber-100" data-testid="picker-runs-hint">
            {t("picker.runsHint", { runs: runsRequired })}
          </p>
        )}
      </section>

      <ul className="space-y-3" role="group" aria-label={t("picker.title")}>
        {pairs.map((pair) => {
          const isSel = selected.has(pair.id);
          const model = pair.shoeType.trim();
          const pairLabel = pair.position.trim()
            ? t("picker.pairLabel", { n: pair.position.trim() })
            : "";
          const colours = colourSlotsFor(pair);
          return (
            <li key={pair.id}>
              <label
                className={`flex cursor-pointer items-start gap-3 ${PANEL} ${
                  isSel ? "border-white/40 bg-white/10" : ""
                }`}
                data-testid={`pair-option-${pair.id}`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  disabled={starting}
                  onChange={() => toggle(pair.id)}
                  className="mt-1 h-5 w-5 shrink-0 accent-white"
                  data-testid={`pair-checkbox-${pair.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-white">{model || pairLabel}</span>
                    {model && pairLabel && (
                      <span className="shrink-0 text-xs text-slate-400">{pairLabel}</span>
                    )}
                  </span>
                  {pair.recipientName.trim() && (
                    <span className="mt-0.5 block text-sm text-slate-300">
                      {pair.recipientName.trim()}
                    </span>
                  )}
                  {colours && <ColourRows colours={colours} />}
                  {(pair.runsRequired ?? 0) > 1 && (pair.runsDone ?? 0) > 0 && (
                    <span
                      className="mt-2 block text-xs font-medium text-amber-200"
                      data-testid={`pair-runs-${pair.id}`}
                    >
                      {t("picker.runsProgress", {
                        finished: pair.runsDone ?? 0,
                        runs: pair.runsRequired ?? 0,
                      })}
                    </span>
                  )}
                  {DEBUG && (
                    <span className="mt-1 block text-xs text-slate-500">
                      {pair.shopifyOrderName || pair.shopifyOrderNumber} · {pair.state} ·{" "}
                      <code className="font-mono">{pair.id}</code>
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {errorMessage && (
        <div
          className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-100"
          data-testid="pair-picker-error"
        >
          {errorMessage}
        </div>
      )}

      <button
        type="button"
        onClick={() => onStart(selectedIds)}
        disabled={!canStart}
        data-testid="pair-picker-start"
        className={PRIMARY_BTN}
      >
        {starting
          ? t("picker.starting")
          : t("picker.continue", { count: selectedIds.length })}
      </button>
    </div>
  );
}
